import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

const BlogDetail = () => {
    const { id } = useParams(); // The slug
    const navigate = useNavigate();
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            try {
                const q = query(collection(db, 'blogs'), where('slug', '==', id));
                const querySnapshot = await getDocs(q);

                if (!querySnapshot.empty) {
                    setBlog(querySnapshot.docs[0].data());
                } else {
                    // If not found, maybe redirect or show "Not Found"
                    console.log('Blog not found!');
                }
            } catch (error) {
                console.error("Error fetching blog:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [id]);

    if (loading) {
        return (
            <>
                <Navbar />
                <div className="min-h-screen flex items-center justify-center bg-white">
                    <div className="text-xl text-neutral-500">Loading Article...</div>
                </div>
                <Footer />
            </>
        );
    }

    if (!blog) {
        return (
            <>
                <Navbar />
                <div className="min-h-screen flex items-center justify-center bg-white flex-col gap-4">
                    <div className="text-2xl font-light text-neutral-800">Article Not Found</div>
                    <button onClick={() => navigate('/blog')} className="text-violet-600 hover:text-violet-800 transition">Return to Blogs</button>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Navbar />

            {/* Hero */}
            <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
                <img
                    src={blog.heroImage}
                    alt={blog.title}
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-black/60" />
                <motion.div
                    className="relative z-10 text-center text-white px-6 w-full max-w-4xl"
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1 }}
                >
                    <h1 className="text-4xl md:text-6xl font-light tracking-wide mb-4">
                        {blog.title}
                    </h1>
                    {blog.date && (
                        <p className="text-lg text-neutral-300 font-light mt-4">{blog.date}</p>
                    )}
                </motion.div>
            </section>

            {/* Blog Content */}
            <section className="bg-white py-24">
                <div className="max-w-4xl mx-auto px-6 space-y-20">

                    <div className="space-y-16">
                        {blog.contentBlocks?.map((block, index) => {
                            if (block.type === 'text') {
                                return (
                                    <div key={index} className="text-lg leading-relaxed text-neutral-700 whitespace-pre-wrap">
                                        {block.content}
                                    </div>
                                );
                            }
                            if (block.type === 'heading') {
                                return (
                                    <h2 key={index} className="text-2xl font-medium text-neutral-900 mt-12 mb-4">
                                        {block.content}
                                    </h2>
                                );
                            }
                            if (block.type === 'quote') {
                                return (
                                    <blockquote key={index} className="border-l-4 border-neutral-300 pl-8 my-10 italic text-xl text-neutral-600 leading-relaxed">
                                        "{block.content}"
                                    </blockquote>
                                );
                            }
                            if (block.type === 'singleImage') {
                                return (
                                    <figure key={index} className="space-y-3 my-12">
                                        <img
                                            src={block.src}
                                            alt={block.caption || 'Blog related image'}
                                            className="w-full h-auto max-h-[600px] object-cover rounded-2xl"
                                        />
                                        {block.caption && (
                                            <figcaption className="text-sm text-neutral-500 text-center">
                                                {block.caption}
                                            </figcaption>
                                        )}
                                    </figure>
                                );
                            }
                            if (block.type === 'imageGrid') {
                                return (
                                    <div key={index} className="grid md:grid-cols-2 gap-8 my-12">
                                        <img
                                            src={block.src1}
                                            alt={'Grid part 1'}
                                            className="w-full h-[400px] object-cover rounded-2xl"
                                        />
                                        <img
                                            src={block.src2}
                                            alt={'Grid part 2'}
                                            className="w-full h-[400px] object-cover rounded-2xl"
                                        />
                                    </div>
                                );
                            }
                            return null;
                        })}
                    </div>

                    {/* Author info */}
                    {blog.author && (
                        <div className="flex items-center gap-6 pt-16 border-t mt-20">
                            {blog.author.image ? (
                                <img
                                    src={blog.author.image}
                                    alt={blog.author.name}
                                    className="w-20 h-20 object-cover rounded-full"
                                />
                            ) : (
                                <div className="w-20 h-20 bg-neutral-200 rounded-full flex items-center justify-center text-neutral-500 font-medium text-xl">
                                    {blog.author.name.charAt(0)}
                                </div>
                            )}
                            <div>
                                <p className="font-medium text-neutral-900 text-lg">{blog.author.name}</p>
                                <p className="text-neutral-500">{blog.author.role}</p>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            <Footer />
        </>
    );
};

export default BlogDetail;
